/**
 * Mutations for a single grocery list with optimistic updates.
 * Pass list and setList from useGroceryList(listId) so UI updates immediately
 * and rolls back on server error.
 */

import { MAX_GROCERY_ITEMS_PER_LIST } from '@/constants/limits';
import { useAuth } from '@/hooks/use-auth';
import { supabase } from '@/lib/supabase';
import type { GroceryList, GroceryListItem } from '@/types';
import { useCallback, useState } from 'react';

function itemsToRow(items: GroceryListItem[]): unknown[] {
  return items.map((item, index) => ({
    id: item.id,
    name: item.name,
    normalizedName: item.normalizedName,
    quantity: item.quantity ?? null,
    unit: item.unit ?? null,
    category: item.category,
    checked: item.checked,
    addedByUserId: item.addedByUserId,
    sourceRecipes: item.sourceRecipes ?? null,
    notes: item.notes ?? null,
    addedAt: item.addedAt,
    checkedAt: item.checkedAt ?? null,
    position: index,
  }));
}

export interface UseGroceryListMutationsOptions {
  listId: string;
  list: GroceryList | null;
  setList: React.Dispatch<React.SetStateAction<GroceryList | null>>;
}

/** Input for adding items (id, addedAt, position, addedByUserId are set by the hook; normalizedName defaulted from name) */
export type AddGroceryListItemInput = Pick<GroceryListItem, 'name' | 'category' | 'checked'> &
  Partial<Pick<GroceryListItem, 'normalizedName' | 'quantity' | 'unit' | 'sourceRecipes' | 'notes' | 'checkedAt'>>;

export interface UseGroceryListMutationsResult {
  updateItem: (itemId: string, updates: Partial<GroceryListItem>) => Promise<void>;
  addItems: (newItems: AddGroceryListItemInput[]) => Promise<void>;
  deleteItem: (itemId: string) => Promise<void>;
  clearCheckedItems: () => Promise<void>;
  clearAllItems: () => Promise<void>;
  updateListName: (newName: string) => Promise<void>;
  reorderItems: (itemsInOrder: GroceryListItem[]) => Promise<void>;
  loading: boolean;
  error: Error | null;
}

/**
 * Mutation helpers with optimistic updates: update local state first,
 * then sync to Supabase; rollback on failure. Real-time subscription
 * will sync server state to other devices.
 */
export function useGroceryListMutations({
  listId,
  list,
  setList,
}: UseGroceryListMutationsOptions): UseGroceryListMutationsResult {
  const { user } = useAuth();
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<Error | null>(null);

  const persistList = useCallback(
    async (nextList: GroceryList): Promise<void> => {
      const { error: updateError } = await supabase
        .from('grocery_lists')
        .update({
          items: itemsToRow(nextList.items),
          name: nextList.name,
          updated_at: new Date().toISOString(),
        })
        .eq('id', listId);

      if (updateError) throw updateError;
    },
    [listId]
  );

  const updateItem = useCallback(
    async (itemId: string, updates: Partial<GroceryListItem>): Promise<void> => {
      if (!list) return;

      const previousList = { ...list, items: [...list.items] };
      const optimisticList: GroceryList = {
        ...list,
        items: list.items.map((item) =>
          item.id === itemId ? { ...item, ...updates } : item
        ),
        updated_at: new Date().toISOString(),
      };
      setList(optimisticList);
      setError(null);

      try {
        await persistList(optimisticList);
      } catch (err) {
        console.error('[useGroceryListMutations] Failed to update item, reverting:', err);
        setList(previousList);
        const e = err instanceof Error ? err : new Error(String(err));
        setError(e);
        throw e;
      }
    },
    [list, setList, persistList]
  );

  const addItems = useCallback(
    async (newItems: AddGroceryListItemInput[]): Promise<void> => {
      if (!list || newItems.length === 0) return;
      if (!user?.id) {
        setError(new Error('You must be signed in to add items'));
        return;
      }
      if (list.items.length + newItems.length > MAX_GROCERY_ITEMS_PER_LIST) {
        const e = new Error(
          `This list can have at most ${MAX_GROCERY_ITEMS_PER_LIST} items. Remove some items to add more.`
        );
        setError(e);
        throw e;
      }

      const now = new Date().toISOString();
      const startPosition = list.items.length;
      const itemsToAdd: GroceryListItem[] = newItems.map((item, i) => ({
        ...item,
        id: `item-${Date.now()}-${i}-${Math.random().toString(36).slice(2, 9)}`,
        normalizedName: item.normalizedName ?? item.name.toLowerCase().trim(),
        addedAt: now,
        addedByUserId: user.id,
        position: startPosition + i,
      }));

      const previousList = { ...list, items: [...list.items] };
      const optimisticList: GroceryList = {
        ...list,
        items: [...list.items, ...itemsToAdd],
        updated_at: now,
      };
      setList(optimisticList);
      setError(null);

      try {
        await persistList(optimisticList);
      } catch (err) {
        console.error('[useGroceryListMutations] Failed to add items, reverting:', err);
        setList(previousList);
        const e = err instanceof Error ? err : new Error(String(err));
        setError(e);
        throw e;
      }
    },
    [list, setList, user?.id, persistList]
  );

  const deleteItem = useCallback(
    async (itemId: string): Promise<void> => {
      if (!list) return;

      const previousList = { ...list, items: [...list.items] };
      const optimisticList: GroceryList = {
        ...list,
        items: list.items.filter((item) => item.id !== itemId),
        updated_at: new Date().toISOString(),
      };
      setList(optimisticList);
      setError(null);

      try {
        await persistList(optimisticList);
      } catch (err) {
        console.error('[useGroceryListMutations] Failed to delete item, reverting:', err);
        setList(previousList);
        const e = err instanceof Error ? err : new Error(String(err));
        setError(e);
        throw e;
      }
    },
    [list, setList, persistList]
  );

  const clearCheckedItems = useCallback(async (): Promise<void> => {
    if (!list) return;

    const previousList = { ...list, items: [...list.items] };
    const optimisticList: GroceryList = {
      ...list,
      items: list.items.filter((item) => !item.checked),
      updated_at: new Date().toISOString(),
    };
    setList(optimisticList);
    setError(null);

    try {
      await persistList(optimisticList);
    } catch (err) {
      console.error('[useGroceryListMutations] Failed to clear checked items, reverting:', err);
      setList(previousList);
      const e = err instanceof Error ? err : new Error(String(err));
      setError(e);
      throw e;
    }
  }, [list, setList, persistList]);

  const clearAllItems = useCallback(async (): Promise<void> => {
    if (!list) return;

    const previousList = { ...list, items: [...list.items] };
    const optimisticList: GroceryList = {
      ...list,
      items: [],
      updated_at: new Date().toISOString(),
    };
    setList(optimisticList);
    setError(null);

    try {
      await persistList(optimisticList);
    } catch (err) {
      console.error('[useGroceryListMutations] Failed to clear all items, reverting:', err);
      setList(previousList);
      const e = err instanceof Error ? err : new Error(String(err));
      setError(e);
      throw e;
    }
  }, [list, setList, persistList]);

  const updateListName = useCallback(
    async (newName: string): Promise<void> => {
      if (!list) return;

      const previousList = { ...list };
      const optimisticList: GroceryList = {
        ...list,
        name: newName,
        updated_at: new Date().toISOString(),
      };
      setList(optimisticList);
      setError(null);

      try {
        await persistList(optimisticList);
      } catch (err) {
        console.error('[useGroceryListMutations] Failed to update list name, reverting:', err);
        setList(previousList);
        const e = err instanceof Error ? err : new Error(String(err));
        setError(e);
        throw e;
      }
    },
    [list, setList, persistList]
  );

  const reorderItems = useCallback(
    async (itemsInOrder: GroceryListItem[]): Promise<void> => {
      if (!list) return;

      const itemsWithPosition = itemsInOrder.map((item, index) => ({ ...item, position: index }));
      const previousList = { ...list, items: [...list.items] };
      const optimisticList: GroceryList = {
        ...list,
        items: itemsWithPosition,
        updated_at: new Date().toISOString(),
      };
      setList(optimisticList);
      setError(null);

      try {
        await persistList(optimisticList);
      } catch (err) {
        console.error('[useGroceryListMutations] Failed to reorder items, reverting:', err);
        setList(previousList);
        const e = err instanceof Error ? err : new Error(String(err));
        setError(e);
        throw e;
      }
    },
    [list, setList, persistList]
  );

  return {
    updateItem,
    addItems,
    deleteItem,
    clearCheckedItems,
    clearAllItems,
    updateListName,
    reorderItems,
    loading,
    error,
  };
}
